'use client';

import { styled } from '@linaria/react';
import PopTopicTimelineFilter from '@fe-news/components/molecules/PopTopicTimelineFilter';
import useFetchInfinitePopTopicList from '@fe-news/hooks/fetchInfiniteList/use-fetch-infinite-pop-topic-list';
import { useContext, useEffect, useRef } from 'react';
import { useDateContext } from '@fe-news/hooks/use-date-context';
import { PopTopicContext } from '@fe-news/context/pop-topic-provider';
import { PopTopicTags } from '@fe-news/constants/pop-topic/default-value';
import Image from 'next/image';
import PopTopicTimelineSkeleton from './PopTopicTimelineSkeleton';
import List from './List';
import { subYears } from 'date-fns';
import { PopTopicListData, PopTopicListOrder } from '@fe-news/constants/pop-topic/type';

const Container = styled.div`
  padding-left: 12px;
`;

const EmptyContainer = styled.div`
  display: flex;
  margin-top: 100px;
  flex-direction: column;
  align-items: center;
`;

const EmptyMessage = styled.p`
  font-size: 16px;
  color: #848d97;
  margin-top: 30px;
  letter-spacing: 0.5px;

  span {
    color: #000000;
  }
`;

const TODAY = new Date();
const ONE_YEAR = 1;
const TIMELINE_LIMIT = 300;
const PopTopicTimeline = () => {
  const { setDates } = useDateContext();
  const { category } = useContext(PopTopicContext);

  const { data, isError, isFetching } = useFetchInfinitePopTopicList({
    isTimeLine: true,
    category,
    limit: TIMELINE_LIMIT,
    order: PopTopicListOrder.EVENT_AT
  });

  const elementReference = useRef<HTMLDivElement | null>(null);

  const isNotEmpty = data && data.length > 0;

  useEffect(() => {
    if (setDates && data.length > 0) {
      const startDate = findLastDataDate(data);
      setDates([startDate, TODAY]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length, setDates]);

  if (isError) {
    return <p>發生錯誤，請重新整理頁面</p>;
  }

  if (isFetching) {
    return <PopTopicTimelineSkeleton />;
  }

  if (!isNotEmpty && !isFetching) {
    return (
      <>
        <PopTopicTimelineFilter />
        <EmptyContainer>
          <Image src='/assets/icons/search-no-data.svg' alt='no-data' height={140} width={150} unoptimized />
          <EmptyMessage>
            您搜尋的 <span>{PopTopicTags[category].name} </span>
            沒有任何結果可顯示，請嘗試其他關鍵字。
          </EmptyMessage>
        </EmptyContainer>
      </>
    );
  }

  return (
    <>
      <PopTopicTimelineFilter />
      {isNotEmpty && (
        <Container ref={elementReference}>
          <List data={data} />
        </Container>
      )}
    </>
  );
};

export default PopTopicTimeline;

const findLastDataDate = (data: PopTopicListData[]) => {
  for (let index = data.length - 1; index >= 0; index--) {
    if (data[index].title !== 'isDefault') {
      return new Date(data[index].onlineAt);
    }
  }
  return subYears(TODAY, ONE_YEAR);
};
